/**
 * /dashboard/travel-preapprovals/$id — G15 travel pre-approval detail.
 *
 * Unlike leave / expense (no single-row GET — detail derived from the
 * list), this module DOES have GET /api/v1/travel-preapprovals/{id}.
 * The handler is access-scoped server-side and 404s an out-of-scope id
 * (no oracle), so a not-mine / not-managed-by-me id renders the same
 * "not found" panel as a deleted row.
 *
 * Layout:
 *   - Status pill at top via <StatusBadge>.
 *   - "Request details" card — title, destination, dates, estimated
 *     budget, purpose, requester (when viewed by an approver / HR).
 *   - "Decision & budget" card — current status + cap + consumed tally
 *     when approved + audit trail (decided by / when / notes), with the
 *     approve / reject / cancel actions row at the bottom.
 *
 * Money in PAISE on the wire; displayed via formatPaise from
 * lib/expense-format (the canonical integer-paise → ₹ renderer).
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import {
  ArrowLeft,
  CalendarRange,
  FileText,
  MapPin,
  Wallet,
} from "lucide-react";
import { useTravelPreapproval } from "@/hooks/use-travel-preapprovals";
import { useDirectory } from "@/hooks/use-directory";
import { toApiError } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/leave-format";
import { formatPaise } from "@/lib/expense-format";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/shared/page-header";
import { StatusBadge } from "@/components/shared/status-badge";
import { TravelDecisionActions } from "@/components/travel/travel-decision-actions";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/travel-preapprovals/$id",
)({
  component: TravelPreapprovalDetailRoute,
});

function Row({
  label,
  value,
}: Readonly<{ label: string; value: React.ReactNode }>) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-input px-3 py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium text-gray-900">
        {value}
      </dd>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

function TravelPreapprovalDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  // A malformed id ("foo") parses to NaN; useTravelPreapproval's
  // `enabled: Number.isFinite(numId) && numId > 0` guard keeps the
  // request from firing, and the page falls through to the "not found"
  // panel — same UX as a deleted / out-of-scope row.
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;

  const { user } = useAuth();
  const q = useTravelPreapproval(numId, validId);
  // Used only for the approver / HR view to label "Submitted by". Defer
  // when the caller is the requester (their own row); the server's own
  // `requester_id` is canonical.
  const dir = useDirectory({
    includeInactive: true,
    enabled: q.data ? user?.user_id !== q.data.requester_id : false,
  });

  const preapproval = q.data;
  const isOwner =
    !!preapproval && user?.user_id === preapproval.requester_id;

  const requesterLabel = preapproval
    ? isOwner
      ? "You"
      : dir.label(preapproval.requester_id)
    : "";

  const approverLabel =
    preapproval && preapproval.approver_id != null
      ? dir.label(preapproval.approver_id)
      : "—";

  return (
    <>
      <PageHeader
        title="Travel pre-approval"
        description={
          preapproval
            ? `Submitted on ${fmtDate(preapproval.created_at)}.`
            : undefined
        }
        actions={
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/travel-preapprovals" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid pre-approval id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid pre-approval id.{" "}
            <Link
              to="/app/$tenantSlug/travel-preapprovals" params={{ tenantSlug }}
              className="ml-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to travel pre-approvals
            </Link>
            .
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert>
          <AlertTitle>Pre-approval not found</AlertTitle>
          <AlertDescription>
            This travel pre-approval doesn&apos;t exist or isn&apos;t
            visible to your role.{" "}
            <Link
              to="/app/$tenantSlug/travel-preapprovals" params={{ tenantSlug }}
              className="ml-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to travel pre-approvals
            </Link>
            .{" "}
            <span className="text-muted-foreground">
              ({toApiError(q.error).message})
            </span>
          </AlertDescription>
        </Alert>
      )}

      {validId && preapproval && (
        <div className="space-y-5">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base">
                    {preapproval.title}
                  </CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {requesterLabel}
                  </p>
                </div>
                <StatusBadge status={preapproval.status} />
              </div>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-3 sm:grid-cols-2">
                <Row
                  label="Destination"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      {preapproval.destination}
                    </span>
                  }
                />
                <Row
                  label="Dates"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
                      {fmtDate(preapproval.start_date)} –{" "}
                      {fmtDate(preapproval.end_date)}
                    </span>
                  }
                />
                <Row
                  label="Estimated budget"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <Wallet className="h-3.5 w-3.5 text-muted-foreground" />
                      {formatPaise(preapproval.estimated_budget_paise)}
                    </span>
                  }
                />
                <Row
                  label="Submitted"
                  value={fmtDateTime(preapproval.created_at)}
                />
              </dl>
              {preapproval.purpose && (
                <div className="mt-4 rounded-md border border-input bg-muted/30 p-3">
                  <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" />
                    Purpose
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                    {preapproval.purpose}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status & decision summary — the honest "timeline":
              the preapproval's own lifecycle fields, NOT a synthetic feed. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Decision &amp; budget
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <dl className="grid gap-3 sm:grid-cols-2">
                <Row
                  label="Current status"
                  value={<StatusBadge status={preapproval.status} />}
                />
                {preapproval.approved_budget_paise != null && (
                  <>
                    <Row
                      label="Approved cap"
                      value={formatPaise(
                        preapproval.approved_budget_paise,
                      )}
                    />
                    <Row
                      label="Consumed so far"
                      value={formatPaise(
                        preapproval.consumed_budget_paise,
                      )}
                    />
                    <Row
                      label="Remaining"
                      value={formatPaise(
                        preapproval.approved_budget_paise -
                          preapproval.consumed_budget_paise,
                      )}
                    />
                  </>
                )}
                {preapproval.decided_at ? (
                  <>
                    <Row
                      label="Decided"
                      value={fmtDateTime(preapproval.decided_at)}
                    />
                    <Row label="Decided by" value={approverLabel} />
                    <Row
                      label="Decision note"
                      value={preapproval.decision_notes || "—"}
                    />
                  </>
                ) : (
                  <div className="rounded-md border border-dashed border-input px-3 py-2 text-sm text-muted-foreground sm:col-span-2">
                    {preapproval.status === "pending"
                      ? "Awaiting a decision from the approver."
                      : preapproval.status === "cancelled"
                        ? "This request was cancelled before any decision."
                        : "No decision recorded."}
                  </div>
                )}
              </dl>

              <div className="pt-1">
                <TravelDecisionActions preapproval={preapproval} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
